'use client';
import type { ForwardedRef } from 'react';

import type { TextInputProps } from '../text-input';

import { cn } from '@polyseme/utils/cn/cn-utils';
import { forwardRef, useState } from 'react';

import { Icon } from '../../icons';
import { TextInput } from '../text-input';

/** Identifier for a built-in password validation rule. */
export type RulesCode =
  | 'lowercase'
  | 'minLength'
  | 'number'
  | 'special'
  | 'uppercase';
/** Function that checks whether a password string satisfies a rule. */
export type RuleValidator = (value: string) => boolean;
/** A single password validation rule with its display label and validation logic. */
export type PasswordRule = {
  code: RulesCode;
  label: string;
  validator: RuleValidator;
};
type PasswordValidatorProps = {
  passwordRules: PasswordRule[];
  value?: string;
};
const PasswordValidator = ({
  passwordRules,
  value,
}: PasswordValidatorProps) => {
  return (
    <div>
      {passwordRules.map(({ code, label, validator }) => {
        const isValid = validator(value || '');
        return (
          <div
            key={`password_rule_${code}`}
            className="flex items-center space-x-1"
          >
            <Icon
              className={cn('text-semantic-error-foreground', {
                'text-semantic-success-foreground': isValid,
              })}
              name={isValid ? 'circle-check' : 'circle-x'}
            />
            <p className="text-sm leading-7 text-muted-foreground">{label}</p>
          </div>
        );
      })}
    </div>
  );
};

PasswordValidator.displayName = 'PasswordValidator';

/**
 * Props for the PasswordInput component.
 *
 * Password field with a visibility toggle and optional real-time validation feedback.
 * When `passwordRules` are provided, each rule is displayed with a colored icon
 * (success/error semantic tokens) that updates as the user types.
 */
export type PasswordInputProps = Omit<
  TextInputProps,
  'autoComplete' | 'value'
> & {
  /** Restricted to password-specific autocomplete values for browser autofill. */
  autoComplete?: 'current-password' | 'new-password';
  /** Validation rules displayed below the input with real-time pass/fail feedback. */
  passwordRules?: PasswordRule[];
  value?: string;
};

export const PasswordInput = forwardRef(
  (
    { autoComplete, passwordRules, ...props }: PasswordInputProps,
    ref: ForwardedRef<HTMLInputElement>
  ) => {
    const [isVisible, setIsVisible] = useState(false);

    return (
      <div className="space-y-2">
        <TextInput
          ref={ref}
          {...props}
          autoComplete={autoComplete}
          rightIcon={isVisible ? 'eye-off' : 'eye'}
          type={isVisible ? 'text' : 'password'}
          onRightIconClick={() => setIsVisible(!isVisible)}
        />
        {!!passwordRules?.length && (
          <PasswordValidator
            passwordRules={passwordRules}
            value={props.value}
          />
        )}
      </div>
    );
  }
);

PasswordInput.displayName = 'PasswordInput';
